import styles from './styles.module.scss';
import Card from './card';
import { useDispatch, useSelector } from 'react-redux';
import { getUnreadThreadsCount } from 'store/selectors/sockets/threads';
import { DRAWER_MESSAGES_TYPES } from 'settings/constants/drawers';
import { useHistory } from 'react-router-dom';
import { lazy, useEffect, useState, Suspense } from 'react';
import { getThirdPartyStats, getThirdPartyTransactionsDocumentsByIds } from 'api/thirdParty';
import { Spinner, PdfViewModal } from 'components';
import { tasksStatusesIds } from 'settings/constants/transactionTasks';
import { getTaskAggregate } from 'api/taskAggregate';
import TasksModal from '../PartnerDashboard/components/TasksModal';
import { getDateOnly, showErrorMessage } from 'helpers';
import { isToday } from 'helpers/date';
import FilesModal from './components/FilesModal';
import { FILE_TYPES } from 'settings/constants/common';
import { previewDocumentEffect } from 'store/effects/transactions';
import {
  changeMessagesDrawerTypeAction,
  openMessagesDrawerAction,
} from 'store/actions/drawers/messages';
import {
  getTransactionEffect,
  getTransactionTaskEffect,
  resetTransactionTaskEffect,
  setTransactionTaskParamsEffect,
  setTransactionTasksLastStateEffect,
} from 'store/effects';

const TaskViewRevamped = lazy(
  () =>
    import(
      'pages/Workshop/Transactions/TransactionTasks/components/Task/ViewRevamped/ViewRevamped'
    ),
);

enum TaskCardType {
  all = 'all',
  today = 'today',
  overdue = 'overdue',
}

const ThirdPartyDashboard = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const unreadThreadsCount = useSelector(getUnreadThreadsCount);
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<any>(undefined);
  const [showTasksModal, setShowTasksModal] = useState<boolean>(false);
  const [tasksLoading, setTaskLoading] = useState<boolean>(false);
  const [tasksToComplete, setTasksToComplete] = useState<any[]>([]);
  const [showTaskViewModal, setShowTaskViewModal] = useState<boolean>(false);
  const [taskType, setTaskType] = useState<TaskCardType>(TaskCardType.all);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const [allFiles, setAllFile] = useState<any[]>([]);
  const [showFilesModal, setShowFilesModal] = useState<boolean>(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);
  const [document, setDocument] = useState<any>(null);

  useEffect(() => {
    fetchThirdPartyStats();
  }, []);

  const fetchThirdPartyStats = async () => {
    try {
      const response = await getThirdPartyStats();
      if (response.status === 200 && response.data?.result) setStats(response.data.result);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleUnreadMessages = () => {
    unreadThreadsCount > 0 && dispatch(openMessagesDrawerAction(true));
    dispatch(
      changeMessagesDrawerTypeAction({
        type: DRAWER_MESSAGES_TYPES.UNREAD,
      }),
    );
  };

  const fetchTasksToComplete = async () => {
    setTaskLoading(true);
    try {
      const cfg = {
        filters: {
          assignedByIds: [],
          assignedToIds: [],
          campaignOnly: false,
          dueFrom: null,
          dueTo: null,
          projectStatuses: [],
          projects: [],
          statuses: [
            tasksStatusesIds.new,
            tasksStatusesIds.inProgress,
            tasksStatusesIds.stuck,
            tasksStatusesIds.overdue,
          ],
          tags: [],
          transactionStatuses: [],
          transactions: [],
        },
      };
      const response = await getTaskAggregate(cfg);
      if (response.status === 200 && response.data?.result?.length)
        setTasksToComplete(response.data.result);
    } catch (e) {
      setTasksToComplete([]);
      showErrorMessage('Unable to fetch tasks');
    }
    setTaskLoading(false);
  };

  const fetchFile = async () => {
    if (!stats?.NewFilesIds?.length) return;

    setFilesLoading(true);
    try {
      const queryString: string = stats.NewFilesIds.join(',');
      const response = await getThirdPartyTransactionsDocumentsByIds(queryString);
      if (response.status === 200 && response.data?.result?.length)
        setAllFile(response.data.result);
    } catch (e) {
      setAllFile([]);
      showErrorMessage('Unable to fetch files');
    }
    setFilesLoading(false);
  };

  const handleTaskViewClick = (task) => {
    const { TransactionId: transactionId, Id: taskId, CampaignId: campaignId } = task;
    transactionId &&
      dispatch(getTransactionEffect({ id: transactionId, include: 'tasks,participants' }));
    dispatch(
      setTransactionTaskParamsEffect({
        taskId,
        transactionId,
        campaignId,
        isTaskDetail: true,
      }),
    );
    dispatch(getTransactionTaskEffect({ transactionId, taskId }));
    dispatch(setTransactionTasksLastStateEffect({ taskId }));
    setShowTasksModal(false);
    setShowTaskViewModal(true);
  };

  const onTaskViewCloseModal = () => {
    setShowTaskViewModal(false);
    setShowTasksModal(true);
    dispatch(
      setTransactionTaskParamsEffect({ campaignId: null, isTaskForm: false, isTaskDetail: false }),
    );
    dispatch(resetTransactionTaskEffect());
  };

  const handleTaskCardClick = (type: TaskCardType) => {
    if (!tasksToComplete.length) {
      fetchTasksToComplete();
    }
    setTaskType(type);
    setShowTasksModal(true);
  };

  const getFilteredTasks = (): any[] => {
    if (taskType === TaskCardType.all) return tasksToComplete;
    else if (taskType === TaskCardType.overdue) {
      return tasksToComplete.filter(
        (task) => task.Status.toLowerCase() === TaskCardType.overdue.toLowerCase(),
      );
    } else if (taskType === TaskCardType.today) {
      return tasksToComplete.filter((task) => {
        let dueDate = task.DueDate ? getDateOnly(task.DueDate, 'M/D/YYYY') : 'N/A';

        return isToday(dueDate);
      });
    } else return [];
  };

  const handleFilesCardClick = () => {
    if (!allFiles.length) {
      fetchFile();
    }
    setShowFilesModal(true);
  };

  const handleFileViewClick = (file) => {
    const isFileValid = isValidFile(file);

    if (!isFileValid) {
      showErrorMessage('File type not supported.');
      return;
    }

    setLoadingPreview(true);

    dispatch(
      previewDocumentEffect({ DocumentVaultUUID: file.DocumentVaultUUID }, {}, (err, response) => {
        setLoadingPreview(false);
        setIsPreviewOpen(true);
        if (err) {
          return showErrorMessage(err);
        }
        setDocument({
          DocumentBuffer: response,
          Filename: file.Title || response.headers['file-name'],
        });
      }),
    );
  };

  const isValidFile = (file): boolean => {
    if (file.Meta?.MimeType === FILE_TYPES.PDF) return true;

    return (
      !file.Meta?.Filename?.endsWith('.zip') &&
      !file.Meta?.Filename?.endsWith('.mp4') &&
      !file.Meta?.Filename?.endsWith('.csv') &&
      !file.Meta?.Filename?.endsWith('.doc') &&
      !file.Meta?.Filename?.endsWith('.docx')
    );
  };

  const onFileViewCloseModal = () => {
    setIsPreviewOpen(false);
    setShowFilesModal(true);
  };

  return (
    <div className={styles.thirdPartyDashboard}>
      <header className={styles.dashboardHeader}>
        <h1 className={styles.heading}>Dashboard</h1>
      </header>
      <div className={styles.content}>
        <div className={styles.row}>
          <Card
            heading="Transactions"
            count={stats?.TransactionsCount ?? 0}
            color="linear-gradient(152.24deg, #928CDA 17.24%, #827CCE 96.1%)"
            clickHandler={() => {
              history.push('/workshop/transactions');
            }}
            loading={loading}
          />
          <Card
            heading="New Messages"
            count={unreadThreadsCount ?? 0}
            color="linear-gradient(153.26deg, #4673D1 16.75%, #3B65BC 105.44%)"
            clickHandler={handleUnreadMessages}
            loading={loading}
          />
          <Card
            heading="New Files"
            count={stats?.NewFilesIds?.length ?? 0}
            color="linear-gradient(168.86deg, #FB913A 8.22%, #E08134 108.27%)"
            clickHandler={handleFilesCardClick}
            loading={loading}
          />
        </div>
        <div className={styles.row}>
          <Card
            heading="Tasks"
            count={stats?.TaskCount ?? 0}
            color="linear-gradient(152.24deg, #51BFE1 17.24%, #21ADD9 96.1%)"
            clickHandler={() => handleTaskCardClick(TaskCardType.all)}
            loading={loading}
          />
          <Card
            heading="Today's Tasks"
            count={stats?.TodayTasksCount ?? 0}
            color="linear-gradient(152.24deg, #00B152 17.24%, #04A451 96.1%)"
            clickHandler={() => handleTaskCardClick(TaskCardType.today)}
            loading={loading}
          />
          <Card
            heading="Overdue Tasks"
            count={stats?.OverdueTasksCount ?? 0}
            color="linear-gradient(152.24deg, #FF576D 17.24%, #ED4F64 96.1%)"
            clickHandler={() => handleTaskCardClick(TaskCardType.overdue)}
            loading={loading}
          />
        </div>
      </div>
      <TasksModal
        loading={tasksLoading}
        handleClose={() => setShowTasksModal(false)}
        tasks={getFilteredTasks()}
        open={showTasksModal}
        handleTaskViewClick={handleTaskViewClick}
      />
      <FilesModal
        loading={filesLoading}
        handleClose={() => setShowFilesModal(false)}
        files={allFiles}
        open={showFilesModal}
        handleFileViewClick={handleFileViewClick}
        loadingPreview={loadingPreview}
      />
      <Suspense fallback={<Spinner />}>
        <TaskViewRevamped
          isOpen={showTaskViewModal}
          onCloseModal={onTaskViewCloseModal}
          className=""
        />
      </Suspense>
      {isPreviewOpen && (
        <PdfViewModal
          isOpen={isPreviewOpen}
          file={document}
          onClose={onFileViewCloseModal}
          showInCohesiveFlow={true}
        />
      )}
    </div>
  );
};

export default ThirdPartyDashboard;
